import type { ReportGeoShare, ReportShare } from '@discovr/types';

const PERCENT = 100;
const DECIMAL_BASE = 10;
// Bars never scale to less than 1%, so a column of tiny shares doesn't fill up.
const MIN_BAR_SCALE = 1;

/** Bars shown in each Global Signal Mix column. */
export const TOP_ROWS = 6;

export interface DonutSegment {
  name: string;
  value: number;
  color: string;
}

export interface SignalMixRow {
  /** ISO code for the flag; empty for cities. */
  code: string;
  name: string;
  /** Percent (0–100). */
  value: number;
}

/**
 * Donut slices in the backend's order, labelled by the backend. `value` is a
 * percent rounded to `digits` decimals. A slice whose key has no colour (the
 * "Not reported" gap) takes `fallbackColor`.
 */
export function toDonutSegments(
  shares: ReportShare[],
  colors: Record<string, string>,
  fallbackColor: string,
  digits: number,
): DonutSegment[] {
  const scale = DECIMAL_BASE ** digits;
  return shares.map((s) => ({
    name: s.label,
    value: Math.round(s.value * PERCENT * scale) / scale,
    color: colors[s.key] ?? fallbackColor,
  }));
}

/** The top rows of a geography list, without its "All other …" row. */
export function topSignalRows(rows: ReportGeoShare[]): SignalMixRow[] {
  return rows
    .filter((r) => !r.remainder)
    .slice(0, TOP_ROWS)
    .map((r) => ({ code: r.code, name: r.name, value: r.share * PERCENT }));
}

/** The value a column's longest bar stands for. */
export function barScale(rows: SignalMixRow[]): number {
  return Math.max(MIN_BAR_SCALE, ...rows.map((r) => r.value));
}
